import { cloneElement, isValidElement } from 'react';
import type { CSSProperties, FC, ReactElement, ReactNode } from 'react';
import { Avatar, Flex } from 'antd';
import { Video } from 'lucide-react';
import type { IconType } from 'react-icons';
import {
    TbBallpen,
    TbBlockquote,
    TbChartInfographic,
    TbChecklist,
    TbH1,
    TbH2,
    TbH3,
    TbH4,
    TbH5,
    TbH6,
    TbInfoCircle,
    TbList,
    TbListNumbers,
    TbPageBreak,
    TbPhoto,
    TbSourceCode,
    TbTable,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { imgLogoStyling } from '@/theme';

import type { MemoSlashCommandItem } from './CommandsList';

const commandIconMap: Record<string, IconType> = {
    blockquote: TbBlockquote,
    bulletedList: TbList,
    callout: TbInfoCircle,
    checklist: TbChecklist,
    codeBlock: TbSourceCode,
    divider: TbPageBreak,
    heading1: TbH1,
    heading2: TbH2,
    heading3: TbH3,
    heading4: TbH4,
    heading5: TbH5,
    heading6: TbH6,
    image: TbPhoto,
    numberedList: TbListNumbers,
    paragraph: TbBallpen,
    reportComponent: TbChartInfographic,
    table: TbTable,
    video: Video,
};

interface MemoSlashCommandIconProps {
    item: MemoSlashCommandItem;
    size?: number;
}

const MemoSlashCommandIcon: FC<MemoSlashCommandIconProps> = ({ item, size = 20 }) => {
    const { token } = useAntDesignTheme();
    const { embedComponentTypeToIconMap, reportComponentTypeToIconMap } = useMaps();
    const applyMenuIconColor = (icon: ReactNode): ReactNode => {
        if (!isValidElement(icon)) {
            return icon;
        }

        const element = icon as ReactElement<{ style?: CSSProperties }>;

        return cloneElement(element, {
            style: { ...element.props.style, color: token.colorText },
        });
    };
    const renderPlainIcon = (icon: ReactNode) => (
        <Flex
            align='center'
            justify='center'
            style={{ color: token.colorText, flex: '0 0 auto', height: size, width: size }}
        >
            {icon}
        </Flex>
    );
    const embedLogo = item.reportComponentType ? embedComponentTypeToIconMap[item.reportComponentType] : undefined;

    if (embedLogo) {
        return (
            <Avatar
                shape='square'
                size={size}
                icon={<img alt={item.title} src={embedLogo} style={{ ...imgLogoStyling, transform: 'scale(0.75)' }} />}
                style={{
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadiusSM,
                    color: token.colorText,
                    flex: '0 0 auto',
                }}
            />
        );
    }

    const reportComponentIcon = item.reportComponentType
        ? reportComponentTypeToIconMap[item.reportComponentType]
        : undefined;

    if (reportComponentIcon) {
        return (
            <Avatar
                shape='square'
                size={size}
                icon={applyMenuIconColor(reportComponentIcon)}
                style={{
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadiusSM,
                    color: token.colorText,
                    flex: '0 0 auto',
                }}
            />
        );
    }

    if (item.section === 'Embeds') {
        return renderPlainIcon(<TbSourceCode size='1rem' />);
    }

    const Icon = item.iconKey ? commandIconMap[item.iconKey] : undefined;

    return renderPlainIcon(Icon ? <Icon size='1rem' /> : <TbBallpen size='1rem' />);
};

export default MemoSlashCommandIcon;
